// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — the live-store → SceneSnapshotRef capture closure (ABB-S7a)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `captureSnapshot(): SceneSnapshotRef` closure the (shipped) S6 `state.read`
// handler consumes via `StateReadHandlerDeps.captureSnapshot`. It maps the LIVE
// `useEditorStore` (the geometryDocument + selection + ABB-S1 revisions) onto the
// structural `SceneSnapshotRef` shape the canonical `projectStateRead` seam +
// the 7 SCENE-QUERY route bodies + summary/selection project.
//
// WHERE IT LIVES (state/, NOT agentBridge/): this is the STORE-COUPLED side of the
// `state.read` DI boundary — it imports `useEditorStore` + drawing-model. The whole
// `agentBridge/` graph is held STORE-FREE by an enforced grep gate
// (`agentBridge/__tests__/commandRun.test.ts` — "no agentBridge source module
// imports editorStore … the ONLY allowed shape is the closure S7 BUILDS in the
// editorStore mount and passes IN as a dep"). So this closure is built HERE (the
// store mount area) and injected into `createStateReadHandler({ captureSnapshot, … })`
// from the OUTSIDE by S7b/S7c. The store-free LEAF seam (`ai/sceneSummaryProjector`)
// + the `ai/sceneQuery/*` bodies stay leaves; this module is their project-OUT source.
//
// [H2] ONE SYNCHRONOUS committed-snapshot: `captureSnapshot` reads the live store
// ONCE, synchronously, and builds the full ref in a single pass per facet — NO
// `await` mid-capture. The handler captures it up front + passes the SAME ref for
// every page of a paginated read (the cursor carries the binding `routeRevisions`,
// so a mid-paging mutation that bumps a counter is detected on the next page).
//
// INERT (Cat-B): merely importing / calling this module mounts NOTHING. S7b/S7c
// wire it into `createStateReadHandler({ captureSnapshot, … })` + enable the
// bridge. Today it has no caller in the app graph.
//
// PER-FACET FIDELITY (ПРИОРИТЕТ #1 — нет упрощений): every REQUIRED field of every
// facet is populated from the real model (a tsc error if one is missed); every
// OPTIONAL field is OMITTED when the source model lacks it (mirroring the Zod
// `.optional()`), never synthesized. `rawChildByType`/`blockName` follow the
// documented contract the seam types pin (the one-truth `buildOrderedTypeCounts`
// order/cap, INSERT-only blockName).
//
// ════════════════════════════════════════════════════════════════════════════════════════════
// `entityBounds` (T1 arm (c)) — the dedicated drawing-model WCS bounds helper, wired in.
// ════════════════════════════════════════════════════════════════════════════════════════════
// The seam pins an OPTIONAL per-entity `SnapshotEntityRef.entityBounds` whose ABSENCE is the
// `boundsKnown=false` marker. This closure now populates it via `computeEntityWcsBounds`
// (`@dxf-ai-editor/drawing-model`, T1 arm (c)) — a REAL WCS bounds source, not the prior
// deferred placeholder.
//
// WHY A NEW HELPER WAS NEEDED (history — the CATEGORY ERROR this fixed): the only WCS-bounds
// source drawing-model exposed before this was `entityModelBounds` (the `viewportMembership`
// helper), built for a DIFFERENT purpose (viewport-clip MEMBERSHIP windowing, paper→model-XY
// clipping) — NOT an accurate WCS bounds source. It was accurate for simple non-extruded
// primitives but WRONG or INCOMPLETE for: Text / MText (insertion-point-only, no glyph
// extents), ANY entity carrying a non-identity OCS extrusion (no group-210 OCS→WCS transform),
// MINSERT grids (one cell, not the column/row expansion), block-defs that nest raw (unsupported)
// children, and Z entirely (it is 2D-XY by design). Rather than ship a box an agent would trust
// as ground truth yet which mis-frames those cases, `entityBounds` was OMITTED for every entity
// (ПРИОРИТЕТ #1 — honest "unknown" over a fabricated extent) until the dedicated helper existed.
//
// THE FIX: `computeEntityWcsBounds` (`packages/drawing-model/src/wcsBounds.ts` + siblings
// `wcsBoundsMath.ts`/`wcsBoundsText.ts`/`wcsBoundsBlock.ts`/`wcsBoundsLeaf.ts`) is that dedicated
// helper — correct Text/MText glyph-extent boxes, the Arbitrary Axis Algorithm OCS→WCS
// transform, full MINSERT grid tiling, recursive block-definition-child resolution, and real Z
// (elevation + thickness-extrusion) for every one of the 17 top-level entity kinds.
// `viewportMembership.ts`'s `entityModelBounds` is UNCHANGED (still the membership pass's own
// fast conservative pre-filter — the two helpers serve different call sites and neither
// replaces the other; see `wcsBounds.ts`'s module header for the full design + documented
// residual approximations).
//
// LAZY, MEMOIZED (mirrors the `geometry` getter's exact perf pattern — @codex T1 finding-1):
// `computeEntityWcsBounds` runs ONLY the first time `entityBounds` is READ (then the result is
// cached on the ref), so a non-`?fields=entityBounds` read — including the `spaces` route's own
// per-space UNION, which walks `snapshot.entities[].entityBounds`
// (`ai/sceneQuery/projectSpaces.ts::buildSpaceGeometry`) — pays this cost ONLY when a route
// actually needs bounds. `entityBounds` is NOT in the `entities` route's `LEAN_CORE_FIELDS`
// (`ai/sceneQuery/projectEntities.ts`) — the SAME non-lean/opt-in treatment `geometry` documents.
//
// PER-SPACE `entityBounds` STAYS UNPOPULATED HERE (deliberately, not an oversight — see
// `toSpaceRef` below): `ai/sceneQuery/projectSpaces.ts`'s `projectSpaces` body ALREADY
// re-derives the per-space union independently from the live per-entity `entityBounds` getters
// (via the one-truth `unionEntityBounds`, `ai/sceneQuery/projectionHelpers.ts`) and explicitly
// STRIPS whatever this closure might attach (to avoid ever serving a stale captured union) — so
// pre-computing it here would force EVERY entity's lazy bounds getter on EVERY
// `captureSnapshot()` call (a `state.read` happens on every companion request), defeating the
// whole point of laziness, for a value the route recomputes and discards anyway.
// ════════════════════════════════════════════════════════════════════════════════════════════
//
// DEP-GRAPH (ПРИОРИТЕТ #2): imports the store + the facet TYPES + the one-truth
// `buildOrderedTypeCounts`/`RAW_CHILD_BY_TYPE_CAP` helper. Dropping the (pre-T1) bounds machinery
// had REMOVED the `entityModelBounds`/`computeBlockDefinitionBounds`/`ALWAYS_INCLUDE`/
// `unionEntityBounds` edges (only reduced edges — never added a cycle); T1 arm (c) now ADDS a
// single new edge back — `computeEntityWcsBounds` (a pure drawing-model function, no new runtime
// edge beyond the existing drawing-model import). It is a SINK — no agentBridge / ai-leaf module
// imports back into it (S7b/S7c reference it from the store mount, not from a leaf). `npx
// depcruise` cycles=0.
//
// T8 osnap-query bridge (Findings 1+4) — ADDS a value edge to
// `scene3d/adapters/typed/blockFromEntity.ts` (+ a type-only edge to `scene3d/adapters/adapterContext.ts`)
// so the `blockFlattenedChildren` getter can reuse the SAME typed block-flatten the Category-B render
// path uses, rather than re-deriving MINSERT/recursion/cycle-guard math a second time. `blockFromEntity`
// is a leaf itself (imports only sibling `scene3d/adapters/typed/*` + `scene3d/types` + drawing-model) —
// no import back into `state/` or `ai/`, so no new cycle. `npm run deps:graph:check` cycles=0.
//
// COORDINATE-ORIGIN CORRECTNESS (own-review finding, PR #528 post-round-17) — `blockFromEntity`'s
// per-kind adapters apply `applyOffset` (RENDERER-ONLY offset-space) to every point they emit under a
// locked session origin (`scene3d/coordinateOrigin.ts`). `blockFlattenedChildren` MUST NOT leak that
// offset-space shift — every other facet this snapshot exposes is TRUE document-world coordinates, and
// the AI-supplied `osnap-query` query point is too. The getter below maps `blockFromEntity`'s output
// through `scene3d/adapters/sceneGeometryWorldCorrection.ts`'s `correctRenderableEntityToWorld` (a
// value edge to `scene3d/adapters/sceneGeometryWorldCorrection.ts`, which itself edges to
// `scene3d/coordinateOrigin.ts` — both leaves, no cycle) BEFORE caching, so the facet's `.geometry` /
// `.bounds` are ALWAYS true-world, identity when no origin is locked (the universal test default).
// ═══════════════════════════════════════════════════════════════════════════

import {
  type BlockBoundsCache,
  type BlockDefinition,
  computeEntityWcsBounds,
  createBlockBoundsCache,
  type DimensionStyle,
  type DrawingDocument,
  type DrawingEntity,
  isAnonymousBlockName,
  type Layout,
  tableAsBlockReference,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

import type {
  SceneSnapshotRef,
  SnapshotBlockRef,
  SnapshotEntityRef,
  SnapshotLayerRef,
  SnapshotRevisions,
  SnapshotSpaceRef,
  SnapshotViewportRef,
} from "../ai/sceneSummaryProjector";
import {
  buildOrderedTypeCounts,
  RAW_CHILD_BY_TYPE_CAP,
  VIEWPORT_ENTITY_TYPE,
} from "../ai/sceneSummaryProjector";
import type { AdapterContext } from "../scene3d/adapters/adapterContext";
import { correctRenderableEntityToWorld } from "../scene3d/adapters/sceneGeometryWorldCorrection";
import { blockFromEntity } from "../scene3d/adapters/typed/blockFromEntity";
import { toEntityGeometry, toEntityGeometryUncapped } from "./captureEntityGeometry";
import { useEditorStore } from "./editorStore";

/**
 * T8 osnap-query bridge (Finding 4) — the degenerate, documented `AdapterContext` the
 * `blockFlattenedChildren` getter below threads into `blockFromEntity`. colorHex /
 * lineweightWorld / linetype / annotative-scale resolution are irrelevant to the GEOMETRY
 * `osnap-query` reads (osnap math only touches `.geometry`/`.bounds`/`.id` — see
 * `osnapQuery.ts`'s module header for the grep-verified proof). `layerColors: new Map()`
 * (the real `LayerColorMap` shape — a `ReadonlyMap<string, LayerColorRecord>`, NOT a plain
 * object) degrades every colour resolution to the resolver's own FALLBACK_HEX, which this
 * route never reads. Every other `AdapterContext` field is `?`-optional with a documented
 * safe default when absent (`adapterContext.ts`'s own doc comments): `blockColor`/
 * `blockLinetype` default to `null`/root-scope, `blocks` defaults to `{}` (unused here —
 * `blockFromEntity` receives the real `doc.blocks` as its own explicit second parameter,
 * not through `ctx.blocks`), and `activeAnnotationScale`/`annoAllVisible` being unset
 * resolves to "no active scale" (`insertAnnotativeHidden`/`resolveTypedAnnotation`'s own
 * documented contract) — every annotative child stays VISIBLE at its master (unscaled)
 * geometry. `osnap-query` does not thread the live document's active CANNOSCALE (out of
 * scope for this fix, documented decision, not an oversight): the block child is still
 * found, just at its un-scaled representation when the drawing has an active scale.
 */
const MINIMAL_ADAPTER_CONTEXT: AdapterContext = {
  layerColors: new Map(),
  cameraZoom: 1,
  zOrderIndex: 0,
};

/**
 * The browser reader COLUMN-SCHEMA version ([L7]) the closure stamps on every
 * captured snapshot — a BROWSER-OWNED constant (there is no store field for it;
 * the projector carries it in the cursor + the page). It is bumped MANUALLY here
 * whenever the projected column shape of ANY route changes (a new field, a
 * removed field, a renamed key) so a continuation cursor minted under an old
 * reader shape false-stales (`projection-version-changed`) rather than mis-paging
 * a re-shaped page. Start at 1 (the S6/SCENE-QUERY column shape this closure feeds).
 *
 * BUMPED to 2 (T1): the `entities` route's projectable column set GREW — the per-type `geometry`
 * facet (`SnapshotEntityGeometry`) is now allowlisted + populated here. A continuation cursor minted
 * under v1 (no geometry column) false-stales (`projection-version-changed`) rather than mis-paging
 * against the v2 shape.
 *
 * BUMPED to 3 (@codex T1 arm(c) round-19 P2 finding — F3): the `entityBounds` facet's COMPUTATION
 * (`computeEntityWcsBounds` / `blockInsertWcsBounds`) fixed several correctness bugs that make it
 * return a REAL, table-bound box for cases that previously returned `null` (omitted) or a wrong
 * value — zero/degenerate INSERT scale no longer collapses the box (F2), a block-child MLEADER's
 * text content is now unioned (F5), a multiline ATTRIB now expands vertically (F6), duplicate
 * ATTDEF tags now match by occurrence instead of last-write-wins (F7), an invisible ATTRIB override
 * no longer widens the box (F8), and a BLOCK-content MLEADER's `blockAttributeReferences[]`
 * overrides are now reflected (F9). `projectEntities.ts` has bound `entityBounds` requests to
 * `routeRevisions.tableRevision` since round-10 (see its own doc), but that binding only catches a
 * DOCUMENT-DATA change (a block-definition edit) between two pages of ONE cursor — it cannot catch
 * a SERVER-CODE change (this fix) that alters what `entityBounds` computes for UNCHANGED document
 * data. A `?fields=entityBounds` cursor minted under the pre-fix server (v2) therefore has a
 * matching `tableRevision` on the post-fix server too (the underlying block table did not change),
 * so `cursorStaleReason`'s STEP 3 `routeRevisions` comparison (`stateReadHandler.ts`) would NOT
 * reject it, silently mixing pre-fix and post-fix `entityBounds` values for the SAME entity across
 * one logical page walk. Bumping `PROJECTION_VERSION` — the browser reader COLUMN-SCHEMA version,
 * orthogonal to any data-revision counter — makes every outstanding v2 cursor false-stale
 * (`projection-version-changed`) on the next page instead.
 *
 * BUMPED to 4 (@codex T1 arm(c) round-22 P2 findings): the `entityBounds` COMPUTATION changed
 * again, for the identical "server-code change, no document-data change, tableRevision alone
 * cannot catch it" reason round-19's bump above documents — a tilted-extrusion ELLIPSE's X/Y now
 * come from the real 3D curve instead of an extrusion-blind 2D formula (finding F1), and a
 * DIMENSION now resolves its NAMED DIMSTYLE table row (this closure's own `dimensionStyles` build
 * below) plus estimates its empty/`"<>"` measurement-text width from the real formatted value
 * (findings F2/F3) — every one of these can change an outstanding v3 cursor's already-served
 * `entityBounds` value for UNCHANGED document data. A regression back to ≤3 would let a pre-fix
 * cursor keep paging post-fix values undetected, the same failure mode round-19's own bump guards
 * against.
 */
export const PROJECTION_VERSION = 4;

// ── per-entity facet ──────────────────────────────────────────────────────────

/**
 * The "not yet computed" sentinel for the lazy `geometry` getter's memo cache. A unique symbol
 * (NOT `undefined`) so a genuinely-projected geometry value can never be confused with the
 * uncomputed state — distinguishing "geometry not yet read" from a (hypothetical) absent geometry.
 */
const UNCOMPUTED = Symbol("captureSnapshot.geometry.uncomputed");

/**
 * Map one typed `DrawingEntity` onto a `SnapshotEntityRef`. Every REQUIRED field is the
 * base-schema field verbatim (`color` is ALREADY the typed `EntityColor` union — no
 * re-shape). Every OPTIONAL field is attached ONLY when present on the source (mirroring
 * the Zod `.optional()`); `extraGroups` is DELIBERATELY omitted (a round-trip-only
 * carrier, not an agent-projectable field — the same omission the seam type documents).
 * `blockName` is attached ONLY for INSERTs (`type === "Block"`, `blockName` field).
 *
 * `entityBounds` (T1 arm (c)) is exposed on EVERY entity ref as a LAZY, MEMOIZED accessor — the
 * SAME pattern `geometry` uses (see below), computed via `computeEntityWcsBounds(entity, {
 * blockDefinitions, resolvedBlockPointsCache })` (`@dxf-ai-editor/drawing-model`) ONLY on first
 * READ. `null` (no computable bounds — a degenerate spline, or an unresolvable/missing/cyclic
 * block INSERT) OMITS the field entirely (`boundsKnown=false`, never a fabricated box); a real box
 * maps `WcsAabb3`'s `{minX,minY,minZ,maxX,maxY,maxZ}` (always fully populated by the helper — Z=0
 * is a genuine WCS fact for a flat entity, not an unknown) verbatim onto the seam's
 * `EntityBoundsExtent`.
 *
 * `resolvedBlockPointsCache` (T1 arm (c) round-13, @codex P2 finding): the CALLER — this
 * function's own caller below, ONE per `captureSnapshot()` pass — builds a single
 * `createBlockBoundsCache()` and threads it into every `toEntityRef` call sharing the same
 * `doc.blocks` table, so the per-block-NAME resolved-LOCAL-points memoization
 * `blockInsertWcsBounds` builds persists ACROSS every INSERT instance of the same block
 * definition on this page, instead of re-walking that definition's geometry from scratch for
 * every instance (`insertCount · definitionSize` → `distinctDefinitionCount · definitionSize`).
 * Safe because `doc.blocks` cannot change mid-capture ([H2] — one synchronous pass) and the cache
 * is purely additive/memoizing, never invalidated mid-use.
 *
 * `dimensionStyles` (@codex T1 arm(c) round-22 P2 finding): the SAME "build once per capture pass,
 * thread into every call" discipline as `resolvedBlockPointsCache` above — this function's own
 * caller builds ONE name-keyed `Record<string, DimensionStyle>` from `doc.styleTables
 * .dimensionStyles` and threads it into `computeEntityWcsBounds`'s `ctx.dimensionStyles` so a
 * `Dimension` entity using a NAMED style (not just per-entity `styleOverrides`) resolves its real
 * DIMASZ/DIMTXT/DIMEXE/DIMEXO/DIMGAP/DIMDEC/DIMPOST — see `wcsBoundsLeaf.ts::dimensionLocal`'s own
 * doc for the full gap this closes. Safe for the identical [H2] reason `resolvedBlockPointsCache` is.
 *
 * `geometry` (T1) is exposed on EVERY entity ref as a LAZY, MEMOIZED accessor (a non-enumerable
 * getter over the typed entity) — NOT an eagerly deep-copied value. `toEntityGeometry(entity)` runs
 * ONLY the first time the field is READ, and the result is cached on the ref. This is the PERF gate
 * (@codex T1 finding-1): the projector reads `e.geometry` ONLY for the `entities` route AND ONLY when
 * the request asked for `geometry` (it is NOT in `LEAN_CORE_FIELDS`), AND `projectFacetFields` reads
 * it ONLY for the rows it actually EMITS (the paginated page slice). So a `state.read` for layers /
 * blocks / spaces / summary / selection — or an `entities` read WITHOUT `geometry` in `?fields` /
 * `?fullField`, or a non-emitted (paged-out) entity — does ZERO geometry deep-copy on a 100k-entity
 * doc. Sort uses `type`/`layer`/`id` only (never `geometry`/`entityBounds`), so ordering never
 * triggers either getter. `entityBounds` follows the identical opt-in gate for `geometry`'s reasons —
 * see the module header's `entityBounds` section for the `spaces`-route exception (it DOES walk every
 * entity's `entityBounds` for its own union, by necessity of that route's contract).
 *
 * When geometry IS requested the output is IDENTICAL to an eager projection — the getter projects the
 * SAME VERBATIM `toEntityGeometry(entity)` (full fidelity, no inference). UNLIKE `entityBounds` (a
 * DERIVED extent), geometry is the entity's RAW defining shape — always available from the
 * typed model, for both inline and compact-backed (streamed) entities (the read-through
 * `space.entities` getter re-materializes the full typed geometry from the compact store). The lazy
 * seam does NOT change the eager, lean `id` / metadata projection (those stay plain own properties);
 * only `geometry`/`entityBounds`/`rawGeometry`/`blockFlattenedChildren` are deferred. Every one of
 * those getters is non-enumerable so a whole-ref enumeration / spread does not force-evaluate any of
 * them (the projector reads them by explicit key, the only access path).
 *
 * `rawGeometry` / `blockFlattenedChildren` (T8 osnap-query bridge, GitHub Codex PR #528 Findings 1+4)
 * — TWO further LAZY, MEMOIZED, NON-ENUMERABLE getters, both INTERNAL-ONLY (never in the `entities`
 * route's field allowlist — see the doc comments on the seam type). `rawGeometry` is the SAME
 * per-type projection as `geometry` but UNCAPPED (`toEntityGeometryUncapped`, no `capGeometry` byte
 * cap — see that function's doc comment for why the cap does not apply to this internal consumer).
 * `blockFlattenedChildren` is attached ONLY for a `type === "Block"` (INSERT) entity — the WORLD-space
 * flattened child renderables via `blockFromEntity` (the SAME typed block-flatten the Category-B
 * render path uses), mapped through `correctRenderableEntityToWorld` (own-review finding, PR #528
 * post-round-17) so the facet is GUARANTEED TRUE document-world coordinates — NEVER the
 * renderer-only offset-space `blockFromEntity`'s per-kind adapters produce internally under a locked
 * session origin (see that helper's module doc + the seam type's own `blockFlattenedChildren` doc
 * comment for the full contract). `blocks` (the document's `BlockDefinition` table) is threaded in as
 * a parameter (closed over by the getters below — both `entityBounds`'s `computeEntityWcsBounds` call
 * and `blockFlattenedChildren`'s `blockFromEntity` call share this SAME table) so this function stays
 * a pure per-entity mapper; the caller (`captureSnapshot()`) already has `doc.blocks` in scope.
 * `resolvedBlockPointsCache` (T1 arm (c) round-13) is the separate per-capture-pass
 * `entityBounds`-only memoization threaded alongside it — see `wcsBoundsBlock.ts::BlockLookupContext`'s
 * doc for why it is safe to share across every entity's `entityBounds` getter.
 */
function toEntityRef(
  entity: DrawingEntity,
  blocks: Record<string, BlockDefinition>,
  resolvedBlockPointsCache: BlockBoundsCache,
  dimensionStyles: Readonly<Record<string, DimensionStyle>>,
): SnapshotEntityRef {
  const ref: {
    -readonly [K in keyof SnapshotEntityRef]?: SnapshotEntityRef[K];
  } = {
    id: entity.id,
    type: entity.type,
    spaceId: entity.spaceId,
    layer: entity.layer,
    color: entity.color,
    linetype: entity.linetype,
    lineweight: entity.lineweight,
    transparency: entity.transparency,
  };
  // OPTIONAL base metadata — attach ONLY when present (mirrors the schema `.optional()`).
  if (entity.linetypeScale !== undefined) ref.linetypeScale = entity.linetypeScale;
  if (entity.ownerHandle !== undefined) ref.ownerHandle = entity.ownerHandle;
  if (entity.materialHandle !== undefined) ref.materialHandle = entity.materialHandle;
  if (entity.plotStyleHandle !== undefined) ref.plotStyleHandle = entity.plotStyleHandle;
  if (entity.visibility !== undefined) ref.visibility = entity.visibility;
  if (entity.shadowMode !== undefined) ref.shadowMode = entity.shadowMode;
  if (entity.extensionDictionaryHandle !== undefined) {
    ref.extensionDictionaryHandle = entity.extensionDictionaryHandle;
  }
  if (entity.arrayGroupId !== undefined) ref.arrayGroupId = entity.arrayGroupId;
  if (entity.arraySourceOfGroup !== undefined) ref.arraySourceOfGroup = entity.arraySourceOfGroup;
  if (entity.arrayMode !== undefined) ref.arrayMode = entity.arrayMode;
  // INSERT-only: the referenced block name (the `blocks` route derives referenceCount from it).
  if (entity.type === "Block") {
    ref.blockName = entity.blockName;
  }
  // `entityBounds` (T1 arm (c)) — the WCS bounds facet, LAZY + MEMOIZED (mirrors `geometry`
  // below): `computeEntityWcsBounds` runs ONLY on first READ. `null` OMITS the field entirely
  // (boundsKnown=false — honest absence, never a fabricated box); a real `WcsAabb3` maps
  // verbatim onto `EntityBoundsExtent` (both are always-populated `{minX,minY,minZ,maxX,maxY,
  // maxZ}` / `{minX,minY,maxX,maxY,minZ?,maxZ?}` — minZ/maxZ are ALWAYS attached, never omitted,
  // per this closure's design: Z=0 is a genuine computed WCS fact, not an unknown).
  let boundsCache: SnapshotEntityRef["entityBounds"] | typeof UNCOMPUTED = UNCOMPUTED;
  Object.defineProperty(ref, "entityBounds", {
    enumerable: false, // a whole-ref spread/enumeration does NOT force-evaluate entityBounds.
    configurable: true,
    get(): SnapshotEntityRef["entityBounds"] {
      if (boundsCache === UNCOMPUTED) {
        const box = computeEntityWcsBounds(entity, { blockDefinitions: blocks, resolvedBlockPointsCache, dimensionStyles });
        boundsCache =
          box === null
            ? undefined
            : { minX: box.minX, minY: box.minY, maxX: box.maxX, maxY: box.maxY, minZ: box.minZ, maxZ: box.maxZ };
      }
      return boundsCache;
    },
  });
  // `geometry` (T1) — the per-type defining geometry, VERBATIM from the typed entity. Attached as a
  // LAZY, MEMOIZED, NON-ENUMERABLE getter (perf — @codex finding-1): `toEntityGeometry(entity)` runs
  // ONLY on first READ (then the result is cached), so a non-geometry route / a non-`?fields=geometry`
  // read / a paged-out row does ZERO geometry deep-copy. The `entities` route gates its EMISSION to
  // `?fields=geometry` (non-lean opt-in) — the only access path that triggers this getter.
  let geometryCache: SnapshotEntityRef["geometry"] | typeof UNCOMPUTED = UNCOMPUTED;
  Object.defineProperty(ref, "geometry", {
    enumerable: false, // a whole-ref spread/enumeration does NOT force-evaluate geometry.
    configurable: true,
    get(): SnapshotEntityRef["geometry"] {
      if (geometryCache === UNCOMPUTED) {
        geometryCache = toEntityGeometry(entity);
      }
      return geometryCache;
    },
  });
  // `rawGeometry` (T8 osnap-query bridge, Finding 1) — the SAME lazy/memoized/non-enumerable
  // discipline as `geometry` above, but via the UNCAPPED `toEntityGeometryUncapped` (no
  // `capGeometry` byte-cap — see that function's doc comment). INTERNAL-ONLY: never gated behind
  // an `entities` route `?fields=` allowlist entry, read only by `ai/sceneQuery/osnapQuery.ts`.
  let rawGeometryCache: SnapshotEntityRef["rawGeometry"] | typeof UNCOMPUTED = UNCOMPUTED;
  Object.defineProperty(ref, "rawGeometry", {
    enumerable: false,
    configurable: true,
    get(): SnapshotEntityRef["rawGeometry"] {
      if (rawGeometryCache === UNCOMPUTED) {
        rawGeometryCache = toEntityGeometryUncapped(entity);
      }
      return rawGeometryCache;
    },
  });
  // `blockFlattenedChildren` (T8 osnap-query bridge, Finding 4; coordinate-origin correction —
  // own-review finding, PR #528 post-round-17) — attached ONLY for a BLOCK-BACKED entity: an
  // INSERT (`"Block"`) and, since @codex PR #785 round 2, an ACAD_TABLE (`"Table"`); every other
  // entity type never gets the property defined at all, so `ref.blockFlattenedChildren` reads back
  // `undefined` for them (never even attempts the flatten).
  //
  // A table's graphics ARE an anonymous `*T<N>` block reference — the cell text and the grid
  // linework live in that definition, not on the ACAD_TABLE record (Autodesk DXF reference,
  // AcDbTable group 2 + 10/11; ezdxf `Table` draws through the same anonymous block). So the table
  // arm reuses the SHARED projection `tableAsBlockReference` — the ONE definition of "this table,
  // read as the INSERT it really is", already used by `entityMetadataIndex.boundsForEntity` and the
  // typed bounds walk — rather than deriving cell/grid geometry a second time here. Its return type
  // IS `BlockInsertEntity`, which is exactly what `blockFromEntity` takes, so the flatten, the
  // world correction and the memoization below are byte-identical for both kinds.
  //
  // This is the PRODUCER half of the osnap-query surface the consumer
  // (`ai/sceneQuery/osnapQuery.ts` — `OSNAP_QUERY_ENTITY_TYPES` + `BLOCK_BACKED_ENTITY_TYPES`)
  // already routes `"Table"` through: without it a real table reached that branch with the facet
  // ABSENT and contributed no snap candidates.
  //
  // `insert` is a `const` binding of type `BlockInsertEntity` at this point — captured by the getter
  // closure below with that type preserved (a `const` capture, unlike a mutable `let`, keeps its
  // narrowed type across the closure boundary).
  if (entity.type === "Block" || entity.type === "Table") {
    const insert =
      entity.type === "Table" ? tableAsBlockReference(entity) : entity;
    let blockChildrenCache: SnapshotEntityRef["blockFlattenedChildren"] | typeof UNCOMPUTED =
      UNCOMPUTED;
    Object.defineProperty(ref, "blockFlattenedChildren", {
      enumerable: false,
      configurable: true,
      get(): SnapshotEntityRef["blockFlattenedChildren"] {
        if (blockChildrenCache === UNCOMPUTED) {
          // Own-review finding (T8 osnap-query bridge, PR #528) — `blockFromEntity`'s
          // per-kind adapters apply `applyOffset` internally (RENDERER-ONLY offset-space,
          // see `sceneGeometryWorldCorrection.ts`'s module header), but every OTHER facet
          // this snapshot exposes (`rawGeometry`, the query point, every top-level entity)
          // is TRUE document-world coordinates. Correct EVERY flattened child back to
          // true-world HERE, at the source, before caching — never leak offset-space out
          // of this getter's documented contract (see the doc comment above + the seam
          // type's own `blockFlattenedChildren` doc comment).
          blockChildrenCache = blockFromEntity(insert, blocks, MINIMAL_ADAPTER_CONTEXT).map(
            correctRenderableEntityToWorld,
          );
        }
        return blockChildrenCache;
      },
    });
  }
  return ref as SnapshotEntityRef;
}

// ── layer facet ───────────────────────────────────────────────────────────────

/**
 * Map one `layerDefinitionSchema` row onto a `SnapshotLayerRef`. `on` is the contract
 * name for the schema's `visible` (the layer On/Off flag). `color` is the RAW ACI INDEX
 * number (group 62) — NOT the EntityColor union (layers carry a bare ACI). `lineweight`
 * is the RAW lineweight ENUM number (group 370, -3 = Default) — distinct from the
 * per-entity lineweight STRING. `flags` is the RAW group-70 bitfield (NOT decoded). The
 * derived `entityCount` is NOT set here (the `layers` body derives it from the entity walk).
 */
function toLayerRef(layer: DrawingDocument["styleTables"]["layers"][number]): SnapshotLayerRef {
  return {
    name: layer.name,
    color: layer.color,
    on: layer.visible,
    frozen: layer.frozen,
    locked: layer.locked,
    linetype: layer.linetype,
    lineweight: layer.lineweight,
    plottable: layer.plottable,
    description: layer.description,
    flags: layer.flags,
    handle: layer.handle,
  };
}

// ── block facet ───────────────────────────────────────────────────────────────

/**
 * Build the per-block unsupported-child-type tally (`entityType → count`) from a block
 * definition's `rawEntityGroups` (the verbatim round-trip-preserved children the typed
 * model could not map). Each `rawEntityGroups` element is ONE raw child carrying its
 * `entityType`; the tally counts occurrences per type. A prototype-free `Map` so a
 * document-supplied `entityType` (e.g. `__proto__`) is a plain key. The companion of the
 * scalar `rawChildCount` (= `rawEntityGroups.length`).
 */
function rawChildTally(def: BlockDefinition): { tally: Map<string, number>; total: number } {
  const tally = new Map<string, number>();
  const groups = def.rawEntityGroups ?? [];
  for (const child of groups) {
    tally.set(child.entityType, (tally.get(child.entityType) ?? 0) + 1);
  }
  return { tally, total: groups.length };
}

/**
 * Map one BLOCK definition (joined with its `blockRecords` metadata) onto a
 * `SnapshotBlockRef`. The block-definition fields (`name`/`basePoint`/`handle`/`flags`/
 * `description`/`xrefPathName`/`xrefStatus`) come from `blockDefinitionSchema`; the
 * record-level fields (`insertionUnits`/`explodable`/`scalable`/`isAnnotative`/
 * `layoutHandle`) come from the matching `blockRecordSchema` entry (name-keyed; a block
 * with no record uses the schema defaults). `derivedIsAnonymous` is the canonical
 * `isAnonymousBlockName` predicate (NOT an ad-hoc inference). `rawChildByType` is the
 * one-truth ordered array (DESC count → entityType ASC, cap RAW_CHILD_BY_TYPE_CAP) with
 * its `*Truncated` companion; `rawChildCount` is the raw-children total. NO inferred
 * `isXref` (the F3 round-trip-preserve trap — the agent classifies from the raw path/status).
 * `referenceCount` is NOT set here (the `blocks` route derives it opt-in from the entity walk).
 */
function toBlockRef(
  def: BlockDefinition,
  record: DrawingDocument["blockRecords"][string] | undefined,
): SnapshotBlockRef {
  const { tally, total } = rawChildTally(def);
  const { ordered, truncated } = buildOrderedTypeCounts(tally, RAW_CHILD_BY_TYPE_CAP);
  const ref: {
    -readonly [K in keyof SnapshotBlockRef]?: SnapshotBlockRef[K];
  } = {
    name: def.name,
    basePoint: { x: def.basePoint.x, y: def.basePoint.y, z: def.basePoint.z },
    handle: def.handle,
    flags: def.flags,
    description: def.description,
    xrefPathName: def.xrefPathName,
    xrefStatus: def.xrefStatus,
    // BLOCK_RECORD metadata (name-keyed). Absent record → the schema defaults (a block
    // with no TABLES record is a degenerate in-memory def; default to the documented values).
    insertionUnits: record?.insertionUnits ?? 0,
    explodable: record?.explodable ?? true,
    scalable: record?.scalable ?? "non-uniform",
    isAnnotative: record?.isAnnotative ?? false,
    derivedIsAnonymous: isAnonymousBlockName(def.name),
    rawChildCount: total,
    rawChildByType: ordered,
  };
  // `*Truncated` rides ONLY when the distinct-child-type count exceeds the cap (else omitted).
  if (truncated) {
    ref.rawChildByTypeTruncated = true;
  }
  // OPTIONAL layout handle (group 340) — present only for paper-space blocks.
  if (record?.layoutHandle !== undefined) {
    ref.layoutHandle = record.layoutHandle;
  }
  return ref as SnapshotBlockRef;
}

// ── space facet ───────────────────────────────────────────────────────────────

/**
 * Map one drawing SPACE (joined with its paper-space `Layout`, when one exists) onto a
 * `SnapshotSpaceRef`. `isActive`/`entityCount` are pre-computed from the already-built entity
 * refs (the `spaces` body re-derives them, but the seam type marks both REQUIRED — so populate
 * them genuinely for a self-consistent snapshot). The per-space `entityBounds` is DELIBERATELY
 * NEVER attached here (see the module header's `entityBounds` section): `ai/sceneQuery/
 * projectSpaces.ts`'s `spaces` body ALREADY re-derives + STRIPS it from the per-entity
 * `entityBounds` union on every request, so eagerly computing it here would force EVERY entity's
 * lazy bounds getter on EVERY `captureSnapshot()` call for a value that is discarded downstream.
 * The PLOT/LIMITS/EXTENTS facets come from the matching `Layout`, resolved by the caller via
 * `layoutsBySpaceId[spaceId] ?? layouts[space.name]` (so a legacy by-name-only doc is not dropped)
 * — but the by-name fallback is GATED to `kind === "layout"` spaces so a `Model` LAYOUT in
 * `layouts["Model"]` cannot leak onto the model tab (Codex S7a P2 round-3). A model / block-edit /
 * block-test / refedit space therefore has no layout → all OPTIONAL layout facets omitted.
 * `handle`/`ownerBlockRecordHandle`/`tabOrder` are the LAYOUT object fields (present for
 * paper-space layouts).
 */
function toSpaceRef(
  space: DrawingDocument["spaces"][number],
  layout: Layout | undefined,
  activeSpaceId: string,
  perSpace: { count: number },
): SnapshotSpaceRef {
  const ref: {
    -readonly [K in keyof SnapshotSpaceRef]?: SnapshotSpaceRef[K];
  } = {
    spaceId: space.id,
    kind: space.kind,
    name: space.name,
    isActive: space.id === activeSpaceId,
    entityCount: perSpace.count,
  };
  // `entityBounds` is deliberately never attached here — see this function's own doc + the
  // module header's `entityBounds` section (the `spaces` route re-derives + strips it itself).
  if (layout !== undefined) {
    // LAYOUT object fields (present for a paper-space layout). `handle`/`ownerBlockRecordHandle`
    // carry the schema default `""` when the layout did not parse one — omit an EMPTY string so
    // an absent handle is OMITTED (the optional-vs-present contract) rather than a fake `""`.
    if (layout.handle.length > 0) ref.handle = layout.handle;
    if (layout.ownerBlockRecordHandle.length > 0) {
      ref.ownerBlockRecordHandle = layout.ownerBlockRecordHandle;
    }
    ref.tabOrder = layout.tabOrder;
    // PLOT facets — from `layout.plotSettings` (page-setup / paper / plot-style names). Omit an
    // EMPTY string (the schema default for an un-configured layout) so an absent name is OMITTED.
    const plot = layout.plotSettings;
    if (plot.pageSetupName.length > 0) ref.pageSetupName = plot.pageSetupName;
    if (plot.paperName.length > 0) ref.paperName = plot.paperName;
    if (plot.plotStyleName.length > 0) ref.plotStyleName = plot.plotStyleName;
    // LIMITS / EXTENTS facets — the LAYOUT-object paper-space framing (present for a layout).
    ref.minimumLimits = { x: layout.minimumLimits.x, y: layout.minimumLimits.y };
    ref.maximumLimits = { x: layout.maximumLimits.x, y: layout.maximumLimits.y };
    ref.minimumExtents = {
      x: layout.minimumExtents.x,
      y: layout.minimumExtents.y,
      z: layout.minimumExtents.z,
    };
    ref.maximumExtents = {
      x: layout.maximumExtents.x,
      y: layout.maximumExtents.y,
      z: layout.maximumExtents.z,
    };
  }
  return ref as SnapshotSpaceRef;
}

// ── viewport facet ──────────────────────────────────────────────────────────────

/**
 * Map one typed VIEWPORT entity onto a `SnapshotViewportRef` — the FULL rich field set,
 * verbatim from `viewportEntitySchema` (the base fields + every viewport-specific REQUIRED
 * field + every OPTIONAL attached only when present). The unbounded lists (`frozenLayers` /
 * `frozenLayerHandles` / `layerOverrideHandles`) are read OFF the entity RAW — NO
 * reconciliation (the parallel XDATA-names vs native-331-handles freeze representations stay
 * unreconciled; the resolve is the deferred S-VIEWPORT-OVERRIDE-RESOLVE slice). The
 * `viewports` route projects only their COUNTS; the nested drill-downs read the full lists.
 */
function toViewportRef(vp: ViewportEntity): SnapshotViewportRef {
  const ref: {
    -readonly [K in keyof SnapshotViewportRef]?: SnapshotViewportRef[K];
  } = {
    // base
    id: vp.id,
    spaceId: vp.spaceId,
    layer: vp.layer,
    color: vp.color,
    linetype: vp.linetype,
    lineweight: vp.lineweight,
    transparency: vp.transparency,
    // viewport-specific REQUIRED (schema `.default`/`.min(1)`/`z.literal`)
    type: vp.type,
    center: { x: vp.center.x, y: vp.center.y },
    width: vp.width,
    height: vp.height,
    viewCenter: { x: vp.viewCenter.x, y: vp.viewCenter.y },
    viewHeight: vp.viewHeight,
    viewDirection: { x: vp.viewDirection.x, y: vp.viewDirection.y, z: vp.viewDirection.z },
    viewTarget: { x: vp.viewTarget.x, y: vp.viewTarget.y, z: vp.viewTarget.z },
    viewTwistAngle: vp.viewTwistAngle,
    lensLength: vp.lensLength,
    frontClipZ: vp.frontClipZ,
    backClipZ: vp.backClipZ,
    viewportStatus: vp.viewportStatus,
    displayLocked: vp.displayLocked,
    // the per-VP frozen-layer NAMES (XDATA) — RAW, verbatim, default `[]`.
    frozenLayers: [...vp.frozenLayers],
  };
  // base OPTIONAL
  if (vp.linetypeScale !== undefined) ref.linetypeScale = vp.linetypeScale;
  if (vp.ownerHandle !== undefined) ref.ownerHandle = vp.ownerHandle;
  if (vp.materialHandle !== undefined) ref.materialHandle = vp.materialHandle;
  if (vp.plotStyleHandle !== undefined) ref.plotStyleHandle = vp.plotStyleHandle;
  if (vp.visibility !== undefined) ref.visibility = vp.visibility;
  if (vp.shadowMode !== undefined) ref.shadowMode = vp.shadowMode;
  if (vp.extensionDictionaryHandle !== undefined) {
    ref.extensionDictionaryHandle = vp.extensionDictionaryHandle;
  }
  if (vp.arrayGroupId !== undefined) ref.arrayGroupId = vp.arrayGroupId;
  if (vp.arraySourceOfGroup !== undefined) ref.arraySourceOfGroup = vp.arraySourceOfGroup;
  if (vp.arrayMode !== undefined) ref.arrayMode = vp.arrayMode;
  // viewport-specific OPTIONAL
  if (vp.viewportCannoscaleName !== undefined) ref.viewportCannoscaleName = vp.viewportCannoscaleName;
  if (vp.viewportCannoscaleFactor !== undefined) {
    ref.viewportCannoscaleFactor = vp.viewportCannoscaleFactor;
  }
  if (vp.viewportAnnoAllVisible !== undefined) ref.viewportAnnoAllVisible = vp.viewportAnnoAllVisible;
  if (vp.brightness !== undefined) ref.brightness = vp.brightness;
  if (vp.contrast !== undefined) ref.contrast = vp.contrast;
  if (vp.viewportId !== undefined) ref.viewportId = vp.viewportId;
  if (vp.statusBitFlags !== undefined) ref.statusBitFlags = vp.statusBitFlags;
  if (vp.snapBase !== undefined) ref.snapBase = { x: vp.snapBase.x, y: vp.snapBase.y };
  if (vp.snapSpacing !== undefined) ref.snapSpacing = { x: vp.snapSpacing.x, y: vp.snapSpacing.y };
  if (vp.gridSpacing !== undefined) ref.gridSpacing = { x: vp.gridSpacing.x, y: vp.gridSpacing.y };
  if (vp.snapRotation !== undefined) ref.snapRotation = vp.snapRotation;
  if (vp.gridMajor !== undefined) ref.gridMajor = vp.gridMajor;
  if (vp.circleZoomPercent !== undefined) ref.circleZoomPercent = vp.circleZoomPercent;
  if (vp.shadePlotMode !== undefined) ref.shadePlotMode = vp.shadePlotMode;
  if (vp.renderMode !== undefined) ref.renderMode = vp.renderMode;
  if (vp.vpOverrideScale !== undefined) {
    ref.vpOverrideScale = { num: vp.vpOverrideScale.num, denom: vp.vpOverrideScale.denom };
  }
  if (vp.vpOverrideUcsName !== undefined) ref.vpOverrideUcsName = vp.vpOverrideUcsName;
  if (vp.clippingBoundaryHandle !== undefined) ref.clippingBoundaryHandle = vp.clippingBoundaryHandle;
  // the native group-331 frozen-layer HANDLES — RAW, OPTIONAL (absent ⇒ no native freeze).
  if (vp.frozenLayerHandles !== undefined) {
    ref.frozenLayerHandles = [...vp.frozenLayerHandles];
  }
  // the per-VP layer-property-override RAW {code,handle} pairs — verbatim, OPTIONAL.
  if (vp.layerOverrideHandles !== undefined) {
    ref.layerOverrideHandles = vp.layerOverrideHandles.map((o) => ({ code: o.code, handle: o.handle }));
  }
  return ref as SnapshotViewportRef;
}

// ── the closure ──────────────────────────────────────────────────────────────

/**
 * Resolve the per-space `{ count }` from the already-built entity refs in ONE pass (O(entities)).
 * Keyed by spaceId. Per-space `entityBounds` is intentionally NOT computed here — it does not
 * force the lazy per-entity `entityBounds` getters (see the module header's `entityBounds`
 * section) — so this only tallies the entity COUNT per space.
 */
function buildPerSpaceGeometry(
  entities: readonly SnapshotEntityRef[],
): Map<string, { count: number }> {
  const out = new Map<string, { count: number }>();
  for (const e of entities) {
    const prior = out.get(e.spaceId);
    if (prior === undefined) {
      out.set(e.spaceId, { count: 1 });
    } else {
      prior.count += 1;
    }
  }
  return out;
}

/**
 * Capture ONE synchronous committed `SceneSnapshotRef` from the LIVE `useEditorStore`
 * ([H2] — read once, no await mid-capture). This is the `StateReadHandlerDeps.captureSnapshot`
 * closure S6's handler consumes: it maps the live `geometryDocument` (entities / layers /
 * blocks / spaces / viewports), the live selection, the active space, and the ABB-S1 revision
 * counters onto the structural snapshot the store-free seam projects.
 *
 * SOURCES (resolved against the real store, ПРИОРИТЕТ #1 — no invented fields):
 *   • documentId            ← `geometryDocument.id`               (model.ts drawingDocumentSchema.id)
 *   • documentEpoch         ← `revisions.documentEpoch`           (revisionModel.RevisionCounters)
 *   • activeSpaceId         ← `geometryDocument.activeSpaceId`    (the canonical doc-level active space)
 *   • selectionIds          ← `state.selection.entityIds`         (ordered shell selection)
 *   • the 5 revisions       ← `revisions.{snapshot,geometry,table,context,selection}Revision`
 *   • projectionVersion     ← the browser-owned `PROJECTION_VERSION` constant (no store field)
 *   • entities/layers/blocks/spaces/viewports ← `geometryDocument` tables (mapped per-facet above)
 *   • per-entity `geometry`  ← `toEntityGeometry(entity)` (T1, captureEntityGeometry.ts) — the
 *     per-type defining geometry VERBATIM from the typed entity; the `entities` route gates its
 *     emission to `?fields=geometry` (non-lean opt-in).
 */
export function captureSnapshot(): SceneSnapshotRef {
  // [H2] ONE synchronous read of the live store — all derivations below read THIS frozen
  // reference (no further getState() call, no await), so the snapshot is internally consistent.
  const store = useEditorStore.getState();
  const doc: DrawingDocument = store.geometryDocument;

  const revisions: SnapshotRevisions = {
    snapshotRevision: store.revisions.snapshotRevision,
    geometryRevision: store.revisions.geometryRevision,
    selectionRevision: store.revisions.selectionRevision,
    tableRevision: store.revisions.tableRevision,
    contextRevision: store.revisions.contextRevision,
  };

  const activeSpaceId = doc.activeSpaceId;

  // ENTITIES — walk every space's entities (the read-through `space.entities` getter
  // materializes from the compact cache transparently). Map each → SnapshotEntityRef (base metadata
  // + INSERT blockName + the lazy `entityBounds`/`geometry` getters, see the module header).
  // VIEWPORTS are also entities, so they appear here too (the `entities` route projects their base
  // metadata); the rich viewport-specific fields live ONLY on the `viewports` facet below.
  //
  // COMPACT-STORE FIDELITY NOTE (Codex S7a finding-1, an UPSTREAM limitation — NOT fixable here):
  // for a STREAMED import (`discardEntitiesAfterStreaming`) the inline `space.entities[]` are
  // discarded and the read-through getter re-materializes each entity from the compact cache via
  // `entityFromCompactStore`, so whatever that cache representation drops is missing here too.
  //
  // The BASE-level half of this note is now CLOSED: `compactStoreBase.ts` carries every
  // `DrawingEntityBase` field without a dedicated column — `linetypeScale` (48), `ownerHandle` (330),
  // `materialHandle` (347), `plotStyleHandle` (390), `visibility` (60), `shadowMode` (284),
  // `extensionDictionaryHandle` and `extraGroups` — in a SPARSE COMMON-METADATA REMAINDER derived
  // from `drawingEntityBaseSchema`, so they survive the streamed rebuild
  // (`__tests__/streamingCommonMetadata.test.ts`).
  //
  // What is still OUTSTANDING is PER-TYPE, one level down: a viewport's `frozenLayerHandles` (native
  // group-331) + `layerOverrideHandles` (per-VP layer overrides — the compact viewport store,
  // `compactComplexStores.ts`, has NO column for either) and, INHERITING the same gap,
  // `sourceFrozenLayerHandles` (@codex #514 P2 "Persist source 331 handles through compact storage"
  // — the full verbatim native-331 order/casing snapshot; this facet doesn't even project it, since
  // it is an internal round-trip carrier, not agent-facing scene state, but its absence after a
  // compact rematerialisation is the SAME root cause). On a compact-backed / streamed drawing those
  // fields read back undefined and this capture omits them — FAITHFULLY reflecting the live document,
  // NOT a capture bug. There is NO non-lossy source to enrich from (the live document IS the lossy
  // compact store; the inline arrays are gone). The real fix is to give the VIEWPORT store the same
  // treatment the base just got, NOT a per-site hack here — fabricating a value would violate
  // ПРИОРИТЕТ #1.
  const entities: SnapshotEntityRef[] = [];
  const viewports: SnapshotViewportRef[] = [];
  // T1 arm (c) round-13 (@codex P2 finding): ONE `BlockBoundsCache` for this whole synchronous
  // capture pass ([H2]), shared by every entity's lazy `entityBounds` getter below — see
  // `toEntityRef`'s doc for why this is safe + what it fixes.
  const resolvedBlockPointsCache = createBlockBoundsCache();
  // @codex T1 arm(c) round-22 P2 finding: ONE name-keyed DIMSTYLE lookup table for this whole
  // synchronous capture pass ([H2]), shared by every entity's lazy `entityBounds` getter below —
  // see `toEntityRef`'s doc for why this is safe + what it fixes. `doc.styleTables.dimensionStyles`
  // is an ARRAY (`DimensionStyle[]`, always populated — `styleTablesSchema`'s own `.default(...)`),
  // keyed here by each style's own `.name` (the same key `DimensionEntity.styleName` round-trips
  // against, per `dimensionStyleSchema`'s doc).
  // @codex T1 arm(c) round-25 P2 finding (Fix 10) — `Object.create(null)`, not a `{}` object
  // literal: `safeRecordLookup`'s `hasOwnProperty.call` READ guard (round-23/24) protects every
  // LOOKUP against an inherited `Object.prototype` member, but the WRITE side here was still a
  // bare `{}` — assigning to a key named EXACTLY `"__proto__"` on a plain object literal does NOT
  // create an own property at all (it invokes the `Object.prototype.__proto__` SETTER, reassigning
  // the object's own prototype instead), so a DXF DIMSTYLE genuinely named `"__proto__"` silently
  // VANISHED from this table entirely — never became a real, retrievable entry — instead of being
  // stored and later resolvable via `resolveDimensionStyleFromContext`/`safeRecordLookup`. A
  // null-prototype object has NO inherited `__proto__` setter to intercept the assignment, so
  // `dimensionStyles["__proto__"] = style` becomes a genuine own property. `safeRecordLookup`'s
  // `Object.prototype.hasOwnProperty.call(record, key)` guard is called EXPLICITLY via `.call`, not
  // through the object's own (now-nonexistent) prototype chain, so it keeps working unchanged
  // against a null-prototype object (verified — see this fix's regression test).
  const dimensionStyles: Record<string, DimensionStyle> = Object.create(null) as Record<string, DimensionStyle>;
  for (const style of doc.styleTables.dimensionStyles) dimensionStyles[style.name] = style;
  for (const space of doc.spaces) {
    for (const entity of space.entities) {
      entities.push(toEntityRef(entity, doc.blocks, resolvedBlockPointsCache, dimensionStyles));
      if (entity.type === VIEWPORT_ENTITY_TYPE) {
        viewports.push(toViewportRef(entity));
      }
    }
  }

  // LAYERS — the full document-level layer table (the `layers` route enumerates ALL).
  const layers: SnapshotLayerRef[] = doc.styleTables.layers.map(toLayerRef);

  // BLOCKS — every block DEFINITION joined with its name-keyed BLOCK_RECORD metadata. The
  // `blocks` route filters the layout pseudo-blocks itself, so include them ALL here.
  const blocks: SnapshotBlockRef[] = Object.values(doc.blocks).map((def) =>
    toBlockRef(def, doc.blockRecords[def.name]),
  );

  // SPACES — model + each paper-space layout, joined with `layoutsBySpaceId` (the stable
  // spaceId→Layout index), FALLING BACK to the by-name `layouts` dict keyed by `space.name` (the
  // AutoCAD tab name). Legacy / older-snapshot docs may carry the LAYOUT only in the by-name
  // `layouts` dict with an empty `layoutsBySpaceId`; the by-id-only lookup would then DROP the
  // layout → the `spaces` route omits the plot/limits/extents facets. This mirrors the established
  // consumer fallback (`exportLayoutPdf.ts::resolvePlotSettings`, `editorStore` plot persistence,
  // `usePrintDialog`): `layoutsBySpaceId[spaceId] ?? layouts[space.name]` (Codex S7a P2).
  //
  // The by-name fallback is GATED to `kind === "layout"` spaces ONLY (Codex S7a P2 round-3): an
  // imported DXF keeps a standard `Model` LAYOUT in `doc.layouts["Model"]` while the importer
  // INTENTIONALLY skips `layoutsBySpaceId["model"]`, so an UNGATED by-name fallback over `space.name`
  // would leak that Model LAYOUT's tabOrder / plot / limits / extents facets onto the MODEL tab.
  // A model / block-edit / block-test / refedit space CANNOT inherit a same-named layout object — only
  // a real paper-space `layout` can. So the model tab (and any non-layout space) skips the by-name
  // fallback entirely → it resolves to `layoutsBySpaceId[space.id]` alone (undefined for model) → no
  // layout facets (correct). A real layout space still gets the by-id index ?? by-name fallback.
  // Pre-compute the per-space entityCount in one pass (per-space entityBounds is intentionally
  // NOT computed here — see `buildPerSpaceGeometry`'s doc + the module header).
  const perSpace = buildPerSpaceGeometry(entities);
  const spaces: SnapshotSpaceRef[] = doc.spaces.map((space) =>
    toSpaceRef(
      space,
      space.kind === "layout"
        ? (doc.layoutsBySpaceId[space.id] ?? doc.layouts?.[space.name])
        : doc.layoutsBySpaceId[space.id],
      activeSpaceId,
      perSpace.get(space.id) ?? { count: 0 },
    ),
  );

  const spaceIds = doc.spaces.map((s) => s.id);

  return {
    entities,
    layers,
    blocks,
    spaces,
    viewports,
    selectionIds: [...store.state.selection.entityIds],
    activeSpaceId,
    spaceIds,
    revisions,
    // The coarse snapshotRevision RESPONSE ANCHOR (= the bound counter; the seam carries it
    // on the page, never as a cursor key — INV-9).
    snapshotRevision: store.revisions.snapshotRevision,
    projectionVersion: PROJECTION_VERSION,
    // The monotonic per-document swap counter (ABB-S1). A live document always carries one
    // (the store seeds `documentEpoch: 1`); the seam type allows `null` only for the
    // unknown-scope neutralizer (which the projector applies, never the capture).
    documentEpoch: store.revisions.documentEpoch,
    documentId: doc.id,
  };
}
